import { BooLogo } from "@/components/BooLogo";
import { ROLE_LABEL } from "@/components/boo/access-copy";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import type { BooRole, OrgRole } from "@/lib/api";
import { authClient, useSession } from "@/lib/auth-client";
import { ORG_ROLE_LABEL } from "@/lib/org-roles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const API_URL = import.meta.env.VITE_API_URL;

type InvitePreview = {
  email: string;
  organizationName: string | null;
  role: string;
};

type InviteErrorData = {
  message: string;
  invitedEmail?: string;
};

/**
 * Fetches a public preview of a pending invitation (email + org name).
 * No auth required since the user may not have signed in yet.
 */
async function fetchInvitePreview(invitationId: string): Promise<InvitePreview | null> {
  try {
    const res = await fetch(
      `${API_URL}/api/auth/organization/invitation-preview?id=${encodeURIComponent(invitationId)}`,
      { credentials: "include" },
    );
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function AcceptInvitationPage() {
  useDocumentTitle("Accept invitation");
  const { invitationId } = useParams({ strict: false }) as { invitationId: string };
  const { data: session, isPending: sessionLoading } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Always fetch the public preview so we can pre-fill the email on the
  // signup/login page and show a better error when the email doesn't match.
  const previewQuery = useQuery({
    queryKey: ["invitation-preview", invitationId],
    enabled: !!invitationId,
    staleTime: Infinity,
    queryFn: () => fetchInvitePreview(invitationId),
  });

  const inviteQuery = useQuery({
    queryKey: ["invitation", invitationId],
    enabled: !!invitationId && !!session?.user,
    queryFn: async () => {
      const res = await authClient.organization.getInvitation({
        query: { id: invitationId },
      });
      if (res.error) {
        const errorData = res.error as unknown as InviteErrorData;
        const err = new Error(errorData.message ?? "Invitation not found");
        // Attach the invited email from the 403 response so we can show it.
        (err as Error & { invitedEmail?: string }).invitedEmail = errorData.invitedEmail;
        throw err;
      }
      return res.data;
    },
  });

  const accept = useMutation({
    mutationFn: async () => {
      const res = await authClient.organization.acceptInvitation({ invitationId });
      if (res.error) throw new Error(res.error.message ?? "Accept failed");
      return res.data;
    },
    onSuccess: async () => {
      // Drop the now-consumed invitation query so the global invalidateQueries
      // below doesn't refetch `getInvitation`, see the row is gone, and flash
      // "Invitation not found" in the gap before `navigate` unmounts the page.
      queryClient.removeQueries({ queryKey: ["invitation", invitationId] });
      await queryClient.invalidateQueries();
      navigate({ to: "/" });
    },
  });

  const reject = useMutation({
    mutationFn: async () => {
      const res = await authClient.organization.rejectInvitation({ invitationId });
      if (res.error) throw new Error(res.error.message ?? "Reject failed");
    },
    onSuccess: () => navigate({ to: "/" }),
  });

  let content: React.ReactNode;

  if (sessionLoading) {
    content = (
      <div className="text-muted-foreground flex items-center gap-2">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    );
  } else if (!session?.user) {
    // Not logged in: show sign-in/sign-up with the invited email
    const invitedEmail = previewQuery.data?.email;
    const orgName = previewQuery.data?.organizationName;

    content = (
      <div className="max-w-md w-full space-y-4 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {orgName ? `Join ${orgName}` : "Accept your invitation"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {invitedEmail
            ? <>Sign in as <strong>{invitedEmail}</strong> to accept this invitation.</>
            : "You need to sign in or create an account to accept this invitation."}
        </p>
        <div className="flex justify-center gap-2">
          <Button asChild variant="outline">
            <Link
              to="/login"
              search={{
                next: `/accept-invitation/${invitationId}`,
                ...(invitedEmail ? { email: invitedEmail } : {}),
              }}
            >
              Sign in
            </Link>
          </Button>
          <Button asChild>
            <Link
              to="/signup"
              search={{
                next: `/accept-invitation/${invitationId}`,
                ...(invitedEmail ? { email: invitedEmail } : {}),
              }}
            >
              Create account
            </Link>
          </Button>
        </div>
      </div>
    );
  } else if (inviteQuery.isLoading) {
    content = (
      <div className="text-muted-foreground flex items-center gap-2">
        <Loader2 className="size-4 animate-spin" /> Loading invitation…
      </div>
    );
  } else if (inviteQuery.isError || !inviteQuery.data) {
    const err = inviteQuery.error as Error & { invitedEmail?: string };
    const invitedEmail = err?.invitedEmail ?? previewQuery.data?.email;
    const isRecipientMismatch =
      invitedEmail &&
      session.user.email.toLowerCase() !== invitedEmail.toLowerCase();

    if (isRecipientMismatch) {
      // The logged-in user's email doesn't match the invite target.
      content = (
        <div className="max-w-md w-full space-y-4 text-center">
          <XCircle className="size-10 text-destructive mx-auto" />
          <h1 className="text-2xl font-semibold tracking-tight">
            Wrong account
          </h1>
          <p className="text-sm text-muted-foreground">
            This invitation was sent to <strong>{invitedEmail}</strong>, but
            you're signed in as <strong>{session.user.email}</strong>.
          </p>
          <p className="text-sm text-muted-foreground">
            Sign in with <strong>{invitedEmail}</strong> to accept.
          </p>
          <Button
            onClick={async () => {
              await authClient.signOut();
              navigate({
                to: "/login",
                search: {
                  next: `/accept-invitation/${invitationId}`,
                  email: invitedEmail,
                },
              });
            }}
          >
            Sign in as {invitedEmail}
          </Button>
        </div>
      );
    } else {
      content = (
        <div className="max-w-md w-full space-y-4 text-center">
          <XCircle className="size-10 text-destructive mx-auto" />
          <h1 className="text-2xl font-semibold tracking-tight">Invitation not found</h1>
          <p className="text-sm text-muted-foreground">
            {err?.message ?? "This invitation may have expired or been cancelled."}
          </p>
          <Button asChild>
            <Link to="/">Go home</Link>
          </Button>
        </div>
      );
    }
  } else {
    const invite = inviteQuery.data as {
      organizationName?: string;
      organizationSlug?: string;
      email: string;
      role: string;
      inviterEmail?: string;
      boos?: Array<{ containerId: string; name: string | null; booRole: BooRole }>;
    };
    const boos = invite.boos ?? [];

    content = (
      <div className="max-w-md w-full space-y-6 text-center">
        <CheckCircle2 className="size-10 text-primary mx-auto" />
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Join {invite.organizationName ?? "this organization"}
          </h1>
          <p className="text-sm text-muted-foreground">
            You've been invited with the <strong>{ORG_ROLE_LABEL[invite.role as OrgRole] ?? invite.role}</strong> role
            {invite.inviterEmail ? ` by ${invite.inviterEmail}` : ""}.
          </p>
          {boos.length > 0 && (
            <ul className="mx-auto flex max-w-sm flex-col gap-1 rounded-lg bg-muted/50 px-4 py-3 text-left text-sm">
              {boos.map((b) => (
                <li key={b.containerId}>
                  You&apos;ll join <strong>{b.name || "a Boo"}</strong> as {ROLE_LABEL[b.booRole] ?? "Standard"}.
                </li>
              ))}
            </ul>
          )}
        </div>

        {accept.isError && (
          <InlineError error={accept.error} />
        )}

        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={reject.isPending || accept.isPending}
            onClick={() => reject.mutate()}
          >
            {reject.isPending && <Loader2 className="size-4 mr-2 animate-spin" />}
            Decline
          </Button>
          <Button
            disabled={accept.isPending || reject.isPending}
            onClick={() => accept.mutate()}
          >
            {accept.isPending && <Loader2 className="size-4 mr-2 animate-spin" />}
            Accept invitation
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-svh items-center justify-center p-6">
      <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        <BooLogo className="size-8 shrink-0 rounded-lg" />
        <span className="text-xl font-semibold tracking-tight">Boo</span>
      </div>
      {content}
    </div>
  );
}
